import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUpdateSettings } from "@/features/settings/hooks/useSettingsMutations";
import {
  securitySettingsValidation,
  type SecuritySettingsFormValues,
} from "@/validations/settings";
import type { SecuritySettings } from "@/types/settings";

interface SecuritySettingsFormProps {
  security?: SecuritySettings;
}

export function SecuritySettingsForm({ security }: SecuritySettingsFormProps) {
  const updateMutation = useUpdateSettings();

  const formMethods = useForm<SecuritySettingsFormValues>({
    values: {
      sessionTimeoutMinutes: security?.sessionTimeoutMinutes ?? 30,
      maxLoginAttempts: security?.maxLoginAttempts ?? 5,
      lockoutDurationMinutes: security?.lockoutDurationMinutes ?? 15,
    },
    resolver: zodResolver(securitySettingsValidation),
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = formMethods;

  const isPending = isSubmitting || updateMutation.isPending;

  async function onSubmit(data: SecuritySettingsFormValues) {
    await updateMutation.mutateAsync({ security: data });
  }

  return (
    <Form {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={control}
          name="sessionTimeoutMinutes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>مهلة الجلسة (دقيقة)</FormLabel>
              <FormControl>
                <Input type="number" min={1} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="maxLoginAttempts"
          render={({ field }) => (
            <FormItem>
              <FormLabel>الحد الأقصى لمحاولات الدخول</FormLabel>
              <FormControl>
                <Input type="number" min={1} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="lockoutDurationMinutes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>مدة الإيقاف بعد تجاوز المحاولات (دقيقة)</FormLabel>
              <FormControl>
                <Input type="number" min={1} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isPending}>
          حفظ إعدادات الأمان
          {isPending && <Loader className="mr-2" />}
        </Button>
      </form>
    </Form>
  );
}
